import axios from 'axios'
import Api from 'App/api'
import stashError from '../utils/stash-error'
import decode from 'jwt-decode'
import { JWT_STORAGE_KEY } from 'portal-modules/common/constants/keys'
import { getSessionCookies } from 'portal-utils/session-cookies'

/**
 * Logs ui errors to remote server.
 * @param {object} error The error object to submit to the server
 * @param {boolean} stash Whether or not to sync error if submission fails. Defaults to true.
 * @returns {object} An axios promise
 */
export const logError = (error, stash = true) => {
  let titleError = error?.section
    ? 'record' + error?.section + 'Error'
    : 'recordError'

  let tokenInfo = []
  if (error?.user_token) {
    const decodedToken = decode(error?.user_token)
    tokenInfo = decodedToken?.data
  }

  return axios({
    method: 'post',
    url: Api.getUrl({ controller: 'errors', action: titleError }),
    data: {
      error_data: {
        error_message: error.message || '',
        stack_trace: error.stack,
        component_name: error.componentName || '',
        token_info: tokenInfo,
        url: window?.location?.href,
        component_stack_trace: error.componentStack || '',
        device_info: {
          appVersion: navigator.appVersion,
          platform: navigator.platform,
          userAgent: navigator.userAgent,
          vendor: navigator.vendor,
        },
      },
    },
    headers: Api.getHeaders(),
  }).catch(() => {
    stash && stashError(error)
  })
}

/**
 * Logs ui errors to remote server.
 * @param {metadata} metadata The metadata object to submit to the server as event data
 * @param {boolean} stash Whether or not to sync error if submission fails. Defaults to true.
 * @returns {object} An axios promise
 */
export const logPlaidError = (metadata, stash = true) => {
  let tokenInfo = []
  if (metadata?.user_token) {
    const decodedToken = decode(metadata?.user_token)
    tokenInfo = decodedToken?.data
  }
  return axios({
    method: 'post',
    url: Api.getUrl({ controller: 'errors', action: 'recordPlaidEvent' }),
    data: {
      event_data: {
        ...metadata,
        token_info: tokenInfo,
        url: window?.location?.href,
        device_info: {
          appVersion: navigator.appVersion,
          platform: navigator.platform,
          userAgent: navigator.userAgent,
          vendor: navigator.vendor,
        },
      },
    },
    headers: Api.getHeaders(),
  }).catch(() => {
    stash && stashError(metadata)
  })
}

export const getProductPermissions = Api.get({
  controller: 'permissions',
  action: 'getProductPermissions',
})

export const getTenantProductPermissions = Api.get({
  controller: 'tenant_permissions',
  action: 'getProductPermissions',
})

export const getLeaseProductPermissions = Api.get({
  controller: 'permissions',
  action: 'getLeaseProductPermissions',
})

export const getStates = Api.get({
  controller: 'address',
  action: 'getStates',
})

export const getLeaseBalanceDetails = Api.get({
  controller: 'balance',
  action: 'getLeaseBalanceDetails',
})

// Creating Axios instance to handle BulK payment API calls.
const AxiosInstance = axios.create();
export const getLeaseBulkBalance = payload => {
  if (typeof payload === 'object') {
    // For bulk Payment we need to do API call for every lease with separate lease token so this payload contains a token
    return AxiosInstance({
      method: 'get',
      url: Api.getUrl({ controller: 'balance', action: 'getLeaseBalance' }),
      data: {},
      headers: {
        'Content-Type': 'application/json',
        'X-Consumer': 'rpweb',
        'X-Consumer-Version': '1.0',
        Authorization: `Bearer ${payload.token}`,
      },
    })
      .then(response => response.data)
      .catch(error => {
        console.log(error)
      })
  }
}

export const getLeaseBalance = Api.get({
  controller: 'balance',
  action: 'getLeaseBalance',
})

export const getContractBalanceDetails = Api.get({
  controller: 'balance',
  action: 'getContractBalanceDetails',
})

export const getContractBalance = Api.get({
  controller: 'balance',
  action: 'getContractBalance',
})

export const getFutureCharges = Api.get({
  controller: 'balance',
  action: 'getScheduledChargeTotal',
})

export const getContactInfoWithSettings = Api.get({
  controller: 'property_contact',
  action: 'getContactInfoWithSettings',
})

export const getPrivacyPolicies = Api.get({
  controller: 'terms',
  action: 'getTermsAndPrivacy',
  skipAuth: true,
})

export const getPropertyProductPermissions = Api.get({
  controller: 'permissions',
  action: 'getPropertyProductPermissions',
})

export const getBulkPaymentsList = Api.post({
  controller: 'payments',
  action: 'list',
})

export const getBulkPaymentsListGroup = Api.post({
  controller: 'payments',
  action: 'listGroup',
})

export const getAvailablePaymentTypes = Api.get({
  controller: 'payments',
  action: 'getAvailablePaymentTypes',
})

export const getPaymentSettings = Api.get({
  controller: 'payments',
  action: 'getSettings',
})

export const postMessage = Api.post({
  controller: 'property_contact',
  action: 'contact',
})

export const verifyApplePayNotification = Api.post({
  controller: 'applepay',
  action: 'verifyApplePayNotification',
  skipAuth: true,
})
